import { Button } from "$components/button/button";
import { ErrorState } from "$components/error-state";
import { Page } from "$components/page";
import PageContent from "$components/page/page-content";
import SearchField from "$components/rhf/search-field";
import type { TableActions } from "$components/table";
import { toast } from "$global-providers/toast-provider";
import { gql, useMutation, useQuery } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Typography } from "@material-ui/core";
import { Add } from "@material-ui/icons";
import { Skeleton } from "@material-ui/lab";
import { clone } from "ramda";
import { useEffect, useRef } from "react";
import {
  FormProvider,
  useFieldArray,
  useForm,
  useWatch,
} from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { newLocationId } from "./_lib/constants";
import { FacilityLocationsTable } from "./_lib/FacilityLocationsTable";
import {
  CREATE_LOCATION_MUTATION,
  FACILITY_LOCATIONS_QUERY,
  FACILITY_QUERY,
  UPDATE_LOCATION_MUTATION,
} from "./_lib/queries";
import validationSchema from "./_lib/schema";
import type {
  CreateLocationResponse,
  FacilityAndDepartmentsResponse,
  FacilityLocationsResponse,
  Location,
  LocationForm,
  LocationInput,
  UpdateLocationResponse,
} from "./_lib/types";

const LOCATIONS_SESSION_KEY = "facilityLocations";

interface FacilityLocationsPageProps {
  facilityId: string;
}

const FacilityLocationsPage = ({ facilityId }: FacilityLocationsPageProps) => {
  const { formatMessage } = useIntl();

  const formMethods = useForm<LocationForm>({
    mode: "onTouched",
    defaultValues: { search: "", locations: [] },
    resolver: yupResolver(validationSchema),
  });

  const ref = useRef<TableActions>();

  const {
    update: updateFormLocation,
    append,
    remove,
    fields,
  } = useFieldArray({
    control: formMethods.control,
    name: "locations",
  });

  const search = useWatch({ control: formMethods.control, name: "search" });

  const { loading: facilityLoading, data: { clinic } = {} } =
    useQuery<FacilityAndDepartmentsResponse>(FACILITY_QUERY, {
      variables: { facilityId },
      onError() {
        toast.error(
          formatMessage({
            id: "facilityLocations.getFacilityAndDepartmentError",
            defaultMessage:
              "Something went wrong while loading facility and department details",
          }),
        );
      },
    });

  const {
    error: facilityLocationsError,
    loading: facilityLocationsLoading,
    data: { facilityLocations } = {},
  } = useQuery<FacilityLocationsResponse>(FACILITY_LOCATIONS_QUERY, {
    variables: { clinicUuids: [facilityId] },
    onCompleted(data) {
      formMethods.setValue("locations", clone(data.facilityLocations));

      // Overwrite a key in local storage to guarantee most up to date list
      sessionStorage.setItem(
        LOCATIONS_SESSION_KEY,
        JSON.stringify(data.facilityLocations.map((location) => location._id)),
      );
    },
    onError() {
      toast.error(
        formatMessage({
          id: "facilityLocations.getLocationsError",
          defaultMessage: "Something went wrong while loading locations",
        }),
      );
    },
  });

  const [createNewLocation] = useMutation<CreateLocationResponse>(
    CREATE_LOCATION_MUTATION,
    {
      update(cache, { data: { createFacilityLocation: newLocation } }) {
        cache.modify({
          fields: {
            facilityLocations(existingLocations = []) {
              const newlyCreatedLocation = cache.writeFragment({
                data: newLocation,
                fragment: gql`
                fragment NewLocation on Location {
                  _id
                  name
                  bedCount
                  clinicUuid
                }
              `,
              });

              return [...existingLocations, newlyCreatedLocation];
            },
          },
        });
      },
      onCompleted(item) {
        // Append location id into session storage
        const currentLocations = sessionStorage.getItem(LOCATIONS_SESSION_KEY);
        const currentLocationsList = currentLocations
          ? JSON.parse(currentLocations)
          : [];
        const { _id: locationId } = item.createFacilityLocation;

        sessionStorage.setItem(
          LOCATIONS_SESSION_KEY,
          JSON.stringify([locationId, ...currentLocationsList]),
        );

        toast.success(
          formatMessage({
            id: "facilityLocations.createLocationSuccess",
            defaultMessage: "Successfully created new location",
          }),
        );
      },
      onError() {
        toast.error(
          formatMessage({
            id: "facilityLocations.createLocationError",
            defaultMessage: "Something went wrong while creating this location",
          }),
        );
      },
    },
  );

  const [updateFacilityLocation] = useMutation<UpdateLocationResponse>(
    UPDATE_LOCATION_MUTATION,
  );

  const { setValue } = formMethods;
  useEffect(() => {
    if (facilityLocations) {
      const searchTerm = search.trim().toLowerCase();
      const newLocationList = searchTerm
        ? facilityLocations.filter((location) =>
            location.name.toLowerCase().includes(searchTerm),
          )
        : facilityLocations;
      setValue("locations", clone(newLocationList));
    }
  }, [search, facilityLocations, setValue]);

  if (facilityLocationsError) {
    return <ErrorState error={facilityLocationsError} />;
  }

  const handleAddNewLocationRow = () => {
    append({
      _id: newLocationId,
      name: "",
      active: true,
      bedCount: null,
    } as Location);

    ref.current.startEditRow(fields.length);
  };

  const handleRemoveNewLocationRow = (index: number) => {
    remove(index);
  };

  const createLocation = async (location: LocationInput) => {
    const result = await createNewLocation({
      variables: {
        input: {
          name: location.name,
          bedCount: location.bedCount,
        },
      },
    });

    return result?.data;
  };

  const updateLocation = async (
    id: string,
    location: LocationInput,
    toggleActivation: boolean,
  ) => {
    try {
      const activationMessageAction = location.active
        ? "activated"
        : "deactivated";

      const result = await updateFacilityLocation({
        variables: {
          id,
          input: {
            name: location.name,
            active: location.active,
            bedCount: location.bedCount,
          },
        },
      });

      toast.success(
        formatMessage(
          {
            id: "facilityLocations.updateLocationSuccess",
            defaultMessage: "Successfully {action} location",
          },
          { action: toggleActivation ? activationMessageAction : "updated" },
        ),
      );

      return result?.data;
    } catch {
      const activationMessageAction = location.active
        ? "activating"
        : "deactivating";

      toast.error(
        formatMessage(
          {
            id: "facilityLocations.updateLocationError",
            defaultMessage: "Something went wrong while {action} this location",
          },
          { action: toggleActivation ? activationMessageAction : "updating" },
        ),
      );

      return null;
    }
  };

  return (
    <Page style={{ paddingTop: "8px" }}>
      <PageContent>
        <Typography variant="h2" data-testid="facility-locations-title">
          {facilityLoading ? <Skeleton width="40%" /> : clinic?.name}
        </Typography>
        <FormProvider {...formMethods}>
          <Box
            display="flex"
            flexDirection={["column", "row"]}
            justifyContent="space-between"
            mt={[3, 5]}
          >
            <Box flex={1} maxWidth={540}>
              <SearchField
                label={formatMessage({
                  id: "facilityLocations.search",
                  defaultMessage: "Search locations",
                })}
                name="search"
                data-testid="facility-locations-search"
                fullWidth
              />
            </Box>
            <Box flexShrink={0} ml={[0, 4]}>
              <Button
                startIcon={<Add />}
                onClick={handleAddNewLocationRow}
                disabled={
                  facilityLocationsLoading ||
                  (fields.length > 0 &&
                    fields[fields.length - 1].id === newLocationId)
                }
              >
                <FormattedMessage
                  id="facilityLocations.addLocation"
                  defaultMessage="Add Location"
                />
              </Button>
            </Box>
          </Box>
          <Box mt={1}>
            <FacilityLocationsTable
              ref={ref}
              cancelAddRow={handleRemoveNewLocationRow}
              createLocation={createLocation}
              updateLocation={updateLocation}
              facilityLocations={facilityLocations}
              loading={facilityLocationsLoading}
              resetLocation={updateFormLocation}
            />
          </Box>
        </FormProvider>
      </PageContent>
    </Page>
  );
};

export default FacilityLocationsPage;
